<template>
  <div
    class="card-metrica"
    :class="{ 'card-metrica--clickable': enlace }"
    @click="irAlEnlace"
  >
    <div class="card-metrica__head">
      <span class="card-metrica__icono"><i :class="icono"></i></span>
      <span class="card-metrica__title">{{ title }}</span>
      <!--
        `.stop` porque el @click de navegar vive en el div de afuera: sin esto,
        tocar el icono de ayuda para leer el tooltip te saca de la pagina. Es
        justo el gesto de quien quiere MAS contexto, no de quien quiere irse.
      -->
      <el-tooltip v-if="ayuda" :content="ayuda" placement="top" effect="dark">
        <i class="el-icon-info card-metrica__info" @click.stop></i>
      </el-tooltip>
      <i v-if="enlace" class="ni ni-bold-right card-metrica__flecha"></i>
    </div>

    <div class="card-metrica__value" :class="{ 'card-metrica__value--na': !hayDato }">
      <template v-if="hayDato">{{ valorFormateado }}</template>
      <template v-else>N/A</template>
    </div>
    <!--
      La pill NUNCA dice si el valor esta bien o mal: los umbrales y el
      sistema A/B/C los define Humberto en su tarea. Aca solo va contexto
      de la medicion, nunca un juicio de valor.
    -->
    <span class="card-metrica__pill" :class="pillClase">{{ pillTexto }}</span>

    <div class="card-metrica__foot">{{ pieTexto }}</div>
  </div>
</template>

<script>
/**
 * Card de una metrica de cocina OD.
 *
 * El estilo replica las MacroKpiCards de dashboard-ops/menu (badge de icono,
 * borde superior verde, etiqueta en mayusculas, numero grande, chip de
 * contexto) para que el proyecto se sienta de la misma familia que el panel
 * interno de Operaciones. Es un cambio de piel, no de datos ni de logica: la
 * fuente, el contrato *_estado y el aislamiento por cocina siguen intactos.
 *
 * Dos diferencias deliberadas frente al original de Operaciones:
 *
 *  1. La pill no juzga. Alli el chip compara contra el periodo anterior con
 *     verde/rojo de "mejor/peor"; aca no hay umbral acordado todavia (eso es
 *     la tarea de Humberto), asi que el chip solo da contexto de la muestra.
 *  2. `null` no es cero. El data lake deja sin medicion casos legitimos (el
 *     dia en curso, los domingos para carta activa) y pintarlos como 0% le
 *     mostraria al catering un mal rendimiento que no ocurrio.
 */
import { Tooltip } from 'element-ui'

export default {
  name: 'CardMetricaCocina',
  // Ver nota en MetricasCocina.vue: Element UI se importa por componente.
  components: { [Tooltip.name]: Tooltip },
  props: {
    title: { type: String, required: true },
    valor: { default: null },
    /** Clase del icono Nucleo para el badge del encabezado. */
    icono: { type: String, default: 'ni ni-chart-bar-32' },
    /** 'ok' | 'sin_dato' | 'muestra_insuficiente' */
    estado: { type: String, default: 'ok' },
    /** 'porcentaje' | 'minutos' | 'estrellas' | 'entero' */
    formato: { type: String, default: 'entero' },
    /** Texto de la pill cuando hay dato (contexto de la muestra). */
    contexto: { type: String, default: '' },
    /** Texto del pie cuando hay dato. */
    pie: { type: String, default: '' },
    /** Motivo especifico cuando no hay medicion. */
    motivo: { type: String, default: 'Sin medición en el periodo' },
    ayuda: { type: String, default: '' },
    /** Ruta del router a la que navega la card al hacer click. Vacio = no clickeable. */
    enlace: { type: String, default: '' }
  },
  computed: {
    hayDato () {
      return this.estado !== 'sin_dato' && this.valor !== null && this.valor !== undefined
    },
    valorFormateado () {
      const v = Number(this.valor)
      if (isNaN(v)) return 'N/A'

      switch (this.formato) {
        case 'porcentaje': return v.toFixed(1) + '%'
        case 'minutos': return v.toFixed(1) + ' min'
        case 'estrellas': return v.toFixed(2)
        case 'entero': return Math.round(v).toLocaleString('es-PE')
        default: return String(v)
      }
    },
    pillTexto () {
      if (!this.hayDato) return 'Sin medición'
      if (this.estado === 'muestra_insuficiente') return 'Muestra pequeña'
      return this.contexto || 'Periodo completo'
    },
    pillClase () {
      if (!this.hayDato) return 'card-metrica__pill--neutra'
      if (this.estado === 'muestra_insuficiente') return 'card-metrica__pill--aviso'
      return 'card-metrica__pill--info'
    },
    pieTexto () {
      if (!this.hayDato) return this.motivo
      if (this.estado === 'muestra_insuficiente') return 'Referencial, pocos datos aún'
      return this.pie || ''
    }
  },
  methods: {
    irAlEnlace () {
      if (this.enlace) this.$router.push(this.enlace)
    }
  }
}
</script>

<style scoped>
/*
  Paleta calcada de dashboard-ops/src/styles/globals.css (--color-mv-*), para
  que esta card sea de la misma familia visual que las MacroKpiCards del
  panel interno de Operaciones. Sin variables CSS porque esta app no las
  define globalmente; se copian los hex directo.
*/
.card-metrica {
  background: #fff;
  border: 1px solid #e8e8e8; /* mv-gray-200 */
  border-top: 3px solid #01662e; /* mv-green-700 */
  border-radius: 14px;
  padding: 18px 20px 16px;
  height: 100%;
  /* Item de un grid con columnas 1fr: por defecto min-width es "auto", que
     equivale al ancho minimo de su contenido, y puede forzar el grid entero
     a estirarse en vez de achicar la card. min-width:0 le permite encogerse
     y envolver su contenido en vez de forzar el ancho de la columna. */
  min-width: 0;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
.card-metrica:hover {
  border-color: #d9d9d9;
  border-top-color: #01662e;
  box-shadow: 0 4px 14px rgba(23, 23, 23, 0.06);
}
.card-metrica__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}
.card-metrica__icono {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #e8f5ec; /* mv-green-50 */
  color: #01662e; /* mv-green-700 */
  font-size: 13px;
  flex-shrink: 0;
}
.card-metrica__title {
  font-size: 11px;
  font-weight: 600;
  color: #737373; /* mv-gray-500 */
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.card-metrica__info { color: #a3a3a3; cursor: help; font-size: 14px; margin-left: auto; }
.card-metrica__value {
  font-size: 28px;
  font-weight: 700;
  color: #171717; /* mv-gray-900 */
  line-height: 1.1;
  margin-bottom: 12px;
  font-variant-numeric: tabular-nums;
}
.card-metrica__value--na { font-size: 22px; color: #a3a3a3; font-weight: 600; }
.card-metrica__pill {
  align-self: flex-start;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.3;
}
.card-metrica__pill--info { background: #e8f5ec; color: #01662e; } /* mv-green-50 / 700 */
.card-metrica__pill--aviso { background: #fff4e5; color: #b7791f; }
.card-metrica__pill--neutra { background: #f5f5f5; color: #737373; } /* mv-gray-100 / 500 */
.card-metrica__foot {
  margin-top: auto;
  padding-top: 12px;
  font-size: 10px;
  color: #a3a3a3; /* mv-gray-400 */
  text-transform: uppercase;
  letter-spacing: 0.03em;
  min-height: 14px;
}
.card-metrica--clickable { cursor: pointer; }
.card-metrica--clickable:hover {
  border-color: #01662e;
  box-shadow: 0 4px 14px rgba(23, 23, 23, 0.1);
}
.card-metrica__flecha { color: #a3a3a3; font-size: 12px; margin-left: auto; }
</style>
